import React, { useEffect, useMemo, useRef, useState } from 'react';
import { signIn, signOut, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { updateUserData } from '../../../store/actions';
import { useAppDispatch } from '../../../store/hooks';
import Swal from 'sweetalert2';
import { attentionIcon, errorBigIcon, toastSuccess } from '../../../assets/icons/icons';
import { IError, IPasswordsVisiblity } from '../../../models/inner-models';
import { decoratePopUpMessage } from '../../../utils/popUpTextDecorator';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { AuthWrapper as Wrapper, Button, FormControl, TextField } from '../../../components/StyledComponents';
import ValidationErrorBlock from '../../../components/Blocks/ValidationErrorBlock/ValidationErrorBlock';
import { ELocalStoreKeys, EMAIL_REGEX, MAX_INPUT_VALUE_LENGTH, PASSWORD_REGEX } from '../../../models/consts';
import { CircularProgress, InputAdornment, Paper, Popper } from '@mui/material';
import IsVisibleButton from '../../../components/Buttons/IsVisibleButton/IsVisibleButton';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { useForm, useWatch } from 'react-hook-form';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import { Helmet } from 'react-helmet';
import '../style.scss';
import '../ResetPassword/style.scss';
import './style.scss';
import trello from '../../../assets/icons/trello-logo-white.svg';
import github from '../../../assets/icons/github-logo-white.svg';
import gitlab from '../../../assets/icons/gitkab-full.svg';
import jira from '../../../assets/icons/Jira_white.svg';
import notion from '../../../assets/icons/notion-logo.svg';
import shopify from '../../../assets/icons/Shopify_white.svg';
import chatgpt from "../../../assets/icons/chatgpt-logo-white.svg";
import claude from "../../../assets/icons/claude-logo-white.svg";
import AuthRightAnimation from "../../../components/AuthRightAnimation";
import AuthHeader from "../../../components/Header/AuthHeader";

const SetupPassword = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isProcessing, setProcessing] = useState<boolean>(false);
  const { register, formState: { errors, isValid }, watch, setValue, control } = useForm({
    mode: 'onChange',
  });
  const [visible, setPasswordVisiblity] = useState<IPasswordsVisiblity>({
    password: false,
  });
  const [notMatchError, setNotMatchError] = useState<boolean>(true);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const passwordAnchorRef = useRef<HTMLDivElement | null>(null);

  const emailUrl = searchParams.get('email') || '';
  const codeUrl = searchParams.get('code') || '';
  const mainPass = useWatch({ control, name: 'password' });
  const passwordValue = mainPass ?? '';
  const firstName = watch('firstName');
  const lastName = watch('lastName');

  useEffect(() => {
    setValue('email', emailUrl);
  }, [emailUrl, setValue]);

  const options = {
    translations: zxcvbnEnPackage.translations,
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
  };
  zxcvbnOptions.setOptions(options);

  const passwordHelpListClassName = useMemo(() => {
    return passwordValue.length ? 'auth-password-help' : 'auth-password-help validation';
  }, [passwordValue.length]);

  const zxcvbnResult = useMemo(() => {
    return passwordValue.length ? zxcvbn(passwordValue) : null;
  }, [passwordValue]);

  useEffect(() => {
    const hasDictionaryWords = Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'dictionary'));
    const hasWeakRepeat = Boolean(
      zxcvbnResult?.sequence?.some((i) => i.pattern === 'repeat') && zxcvbnResult?.score === 0
    );

    if (PASSWORD_REGEX.test(passwordValue) && !(hasDictionaryWords || hasWeakRepeat)) {
      setNotMatchError(false);
    } else {
      setNotMatchError(true);
    }
  }, [passwordValue, zxcvbnResult]);

  const handleClickShowPassword = (field: keyof IPasswordsVisiblity) => () => {
    setPasswordVisiblity({ ...visible, [field]: !visible[field] });
  };

  const handlePasswordSetup = async () => {
    const data = {
      lastName,
      firstName,
    };
    setProcessing(true);
    try {
      const response = await signIn({
        username: emailUrl,
        password: codeUrl,
      });

      if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const confirmResponse = await confirmSignIn({
          challengeResponse: mainPass,
          options: {
            userAttributes: {
              name: firstName,
              family_name: lastName,
            },
          },
        });
        if (confirmResponse.isSignedIn) {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();
          if (idToken) {
            localStorage.setItem(ELocalStoreKeys.TOKEN, idToken);
          }
          await dispatch(updateUserData(data));
          await signOut();
          localStorage.clear();
        }
      } else {
        console.log('Login successful or invalid link', response);
      }
      await Swal.fire({
        title: 'Welcome aboard!',
        text: "Your account setup is complete, and you're all set to go. Dive in and explore everything we have to offer!",
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });

      navigate('/auth/login', { replace: true });
      setProcessing(false);
    } catch (err) {
      const error = err as IError;
      setProcessing(false);
      console.log(error);
      Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>BackupLABS Setup user</title>
        <meta name="title" content="BackupLABS Setup user" />
        <meta name="description" content="Setup new user" />
      </Helmet>
      <div className="auth-login auth-login-page">
        <div className="auth-login-left">
          <AuthHeader/>
          <Wrapper className="setup-password auth-new-form" id="invite-user-setup">
            <div className="auth-title">{translate('forms.setup_user.page_title')}</div>
            <div className="notification-text">
              <img src={attentionIcon} loading="lazy" alt="" />
              <div>{translate('forms.setup_user.desc')}</div>
            </div>
            <div id="invite-user-setup-form">
              <FormControl className="form-row new-reset-password__recover-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.email')}</div>
                  {!!errors.email?.message && (
                    <span className="auth-form__field-error">{errors.email.message}</span>
                  )}
                </div>
                <div className="auth-form__input-block">
                  <TextField
                    className="auth-mui-textfield"
                    placeholder={translate('forms.common.email')}
                    required
                    id="email"
                    type="email"
                    disabled
                    {...register('email', {
                      required: translate('forms.common.required') as string,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: translate('forms.common.invalid_email'),
                      },
                    })}
                    error={!!errors.email}
                  />
                </div>
              </FormControl>

              <FormControl className="form-row new-reset-password__recover-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.first_name')}</div>
                  {!!errors.firstName?.message && (
                    <span className="auth-form__field-error">{errors.firstName.message}</span>
                  )}
                </div>
                <div className="auth-form__input-block">
                  <TextField
                    className="auth-mui-textfield"
                    placeholder={translate('forms.common.first_name')}
                    required
                    id="first-name"
                    inputProps={{ maxLength: MAX_INPUT_VALUE_LENGTH }}
                    {...register('firstName', {
                      required: translate('forms.common.required') as string,
                      maxLength: {
                        value: MAX_INPUT_VALUE_LENGTH,
                        message: translate('forms.common.max_length50'),
                      },
                    })}
                    error={!!errors.firstName}
                  />
                </div>
              </FormControl>

              <FormControl className="form-row new-reset-password__recover-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.last_name')}</div>
                  {!!errors.lastName?.message && (
                    <span className="auth-form__field-error">{errors.lastName.message}</span>
                  )}
                </div>
                <div className="auth-form__input-block">
                  <TextField
                    className="auth-mui-textfield"
                    placeholder={translate('forms.common.last_name')}
                    required
                    id="last-name"
                    inputProps={{ maxLength: MAX_INPUT_VALUE_LENGTH }}
                    {...register('lastName', {
                      required: translate('forms.common.required') as string,
                      maxLength: {
                        value: MAX_INPUT_VALUE_LENGTH,
                        message: translate('forms.common.max_length50'),
                      },
                    })}
                    error={!!errors.lastName}
                  />
                </div>
              </FormControl>

              <FormControl className="form-row new-reset-password__recover-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.recover_password.new_pass')}</div>
                  {!!errors.password?.message && (
                    <span className="auth-form__field-error">{errors.password.message}</span>
                  )}
                </div>
                <div
                  className="auth-form__input-block"
                  ref={passwordAnchorRef}
                  onFocusCapture={() => setIsPasswordFocused(true)}
                  onBlurCapture={(e) => {
                    const next = e.relatedTarget as Node | null;
                    if (!e.currentTarget.contains(next)) {
                      setIsPasswordFocused(false);
                    }
                  }}
                >
                  <TextField
                    className="auth-mui-passfield"
                    placeholder={translate('forms.common.password')}
                    required
                    id="password"
                    type={visible.password ? 'text' : 'password'}
                    {...register('password', {
                      required: translate('forms.common.required') as string,
                      maxLength: {
                        value: MAX_INPUT_VALUE_LENGTH,
                        message: translate('forms.common.max_length50'),
                      },
                    })}
                    error={!!errors.password || (passwordValue.length > 0 && notMatchError)}
                    inputProps={{ maxLength: MAX_INPUT_VALUE_LENGTH }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IsVisibleButton
                            isPrimary
                            onClick={handleClickShowPassword('password')}
                            isNowVisible={visible.password}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.password && (
                    <ValidationErrorBlock errorMessage={errors.password.message as string} />
                  )}
                  <Popper
                    open={isPasswordFocused && passwordValue.length > 0}
                    anchorEl={passwordAnchorRef.current}
                    placement="top-start"
                    modifiers={[{ name: 'offset', options: { offset: [0, -12] } }]}
                    className="auth-password-rules-popper"
                  >
                    <Paper elevation={0} className="auth-password-rules-tooltip">
                      <ul className={passwordHelpListClassName}>
                        <li className={passwordValue.length > 5 ? 'success' : 'error'}>6 characters minimum</li>
                        <li
                          className={
                            Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'dictionary')) ||
                            (Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'repeat')) &&
                              zxcvbnResult?.score === 0)
                              ? 'error'
                              : 'success'
                          }
                        >
                          No dictionary words, repetitions and commonly used passwords
                        </li>
                        <li className={!/\s/.test(passwordValue) ? 'success' : 'error'}>No spaces</li>
                        <li className={/^(.*[0-9].*)$/.test(passwordValue) ? 'success' : 'error'}>One number</li>
                        <li
                          className={/^(?=.*[a-z])(?=.*[A-Z]).*$/.test(passwordValue) ? 'success' : 'error'}
                        >
                          At least one uppercase and one lowercase
                        </li>
                        {(!PASSWORD_REGEX.test(passwordValue) ||
                          Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'dictionary')) ||
                          (Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'repeat')) &&
                            zxcvbnResult?.score === 0)) && (
                          <li className="error">{translate('forms.common.invalid_password')}</li>
                        )}
                      </ul>
                    </Paper>
                  </Popper>
                </div>
              </FormControl>

              <div className="action-row">
                <Button
                  className="primary-button w-100"
                  onClick={handlePasswordSetup}
                  disabled={!isValid || notMatchError || isProcessing}
                >
                  {isProcessing && (
                    <div className="small-spinner-wrapper">
                      <CircularProgress color="inherit" className="auth-btn-spinner" />
                    </div>
                  )}
                  {isProcessing
                    ? translate('notifications.choices.processing')
                    : translate('notifications.choices.save')}
                </Button>
              </div>
            </div>
          </Wrapper>
        </div>
        <div className="auth-login-right">
          <AuthRightAnimation/>
        </div>
      </div>
    </>
  );
};

export default SetupPassword;
